import clsx from 'clsx';
import {
  type CSSProperties,
  forwardRef,
  Fragment,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  SIDEBAR_EXPANDABLE_LIST_GAP,
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y,
} from '../../modules/app-sidebar/views/sidebar-layout';
import * as styles from './index.css';

export type SidebarExpandablePlacement = 'top' | 'bottom';

type SidebarExpandableMotionStyle = CSSProperties & {
  '--sidebar-expandable-expand-duration'?: string;
  '--sidebar-expandable-fade-duration'?: string;
  '--sidebar-expandable-header-height'?: string;
};

type SidebarExpandableContentFrameStyle = CSSProperties & {
  '--sidebar-expandable-content-height'?: string;
};

const REFERENCE_ROW_HEIGHT_PX = 30;
const REFERENCE_ROW_COUNT = 4;
const REFERENCE_DISTANCE_PX =
  REFERENCE_ROW_COUNT * REFERENCE_ROW_HEIGHT_PX +
  (REFERENCE_ROW_COUNT - 1) * SIDEBAR_EXPANDABLE_LIST_GAP +
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y;
const REFERENCE_DURATION_MS = 180;
const MIN_EXPAND_DURATION_MS = 120;
const FADE_DURATION_RATIO = 140 / REFERENCE_DURATION_MS;

const getExpandDurationMs = (distance: number | undefined) => {
  const normalizedDistance =
    typeof distance === 'number' && Number.isFinite(distance) && distance > 0
      ? distance
      : REFERENCE_DISTANCE_PX;
  const speedPxPerMs = REFERENCE_DISTANCE_PX / REFERENCE_DURATION_MS;

  return Math.max(
    MIN_EXPAND_DURATION_MS,
    Math.round(normalizedDistance / speedPxPerMs)
  );
};

const createMotionStyle = (
  animationDistance: number | undefined,
  headerHeight: number | undefined,
  style?: CSSProperties
): SidebarExpandableMotionStyle => {
  const expandDurationMs = getExpandDurationMs(animationDistance);
  const fadeDurationMs = Math.max(
    MIN_EXPAND_DURATION_MS,
    Math.round(expandDurationMs * FADE_DURATION_RATIO)
  );

  return {
    ...style,
    '--sidebar-expandable-expand-duration': `${expandDurationMs}ms`,
    '--sidebar-expandable-fade-duration': `${fadeDurationMs}ms`,
    ...(typeof headerHeight === 'number' &&
    Number.isFinite(headerHeight) &&
    headerHeight > 0
      ? { '--sidebar-expandable-header-height': `${headerHeight}px` }
      : {}),
  };
};

const headerInteractiveTargetSelector = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[data-sidebar-expandable-header-interactive="true"]',
].join(',');

const isHeaderInteractiveTarget = (
  target: EventTarget | null,
  currentTarget: HTMLElement
) => {
  if (!(target instanceof Element)) return false;

  const interactiveElement = target.closest(headerInteractiveTargetSelector);
  return (
    interactiveElement !== null &&
    interactiveElement !== currentTarget &&
    currentTarget.contains(interactiveElement)
  );
};

type SidebarExpandableGroupProps = PropsWithChildren<{
  animationDistance?: number;
  className?: string;
  expanded?: boolean;
  fillLayout?: 'single-top' | 'single-top-fill';
  headerHeight?: number;
  placement?: SidebarExpandablePlacement;
  style?: CSSProperties;
}>;

export const SidebarExpandableGroup = forwardRef<
  HTMLDivElement,
  SidebarExpandableGroupProps
>(
  (
    {
      animationDistance,
      children,
      className,
      expanded,
      fillLayout,
      headerHeight,
      placement = 'bottom',
      style,
    },
    ref
  ) => {
    const motionStyle = createMotionStyle(
      animationDistance,
      headerHeight,
      style
    );

    return (
      <div
        ref={ref}
        className={clsx(styles.group, className)}
        data-expanded={expanded}
        data-fill-layout={fillLayout}
        data-placement={placement}
        style={motionStyle}
      >
        {children}
      </div>
    );
  }
);

SidebarExpandableGroup.displayName = 'SidebarExpandableGroup';

type SidebarExpandableSectionProps = PropsWithChildren<{
  title: ReactNode;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  action?: ReactNode;
  className?: string;
  collapsedSummary?: ReactNode;
  contentClassName?: string;
  contentHeight?: number;
  contentPadding?: 'default' | 'none';
  contentStyle?: CSSProperties;
  headerClassName?: string;
  headerRef?: Ref<HTMLDivElement>;
  animationDistance?: number;
  headerHeight?: number;
  onContentHeightCommit?: (height: number) => void;
  onContentHeightChange?: (height: number) => void;
  placement?: SidebarExpandablePlacement;
  resizable?: boolean;
  onResizeActiveChange?: (active: boolean) => void;
  resizeHitArea?: 'inside' | 'inside-and-after';
  testId?: string;
  titleClassName?: string;
}>;

export const SidebarExpandableSection = ({
  title,
  expanded,
  onExpandedChange,
  action,
  children,
  className,
  collapsedSummary,
  contentClassName,
  contentHeight,
  contentPadding = 'default',
  contentStyle,
  headerClassName,
  headerRef,
  animationDistance,
  headerHeight,
  onContentHeightCommit,
  onContentHeightChange,
  onResizeActiveChange,
  placement = 'bottom',
  resizable = false,
  resizeHitArea = 'inside',
  testId,
  titleClassName,
}: SidebarExpandableSectionProps) => {
  const resizeStartRef = useRef<{
    height: number;
    pointerY: number;
  } | null>(null);
  const resizeCurrentHeightRef = useRef<number | null>(null);
  const resizePendingCommitHeightRef = useRef<number | null>(null);
  const [resizing, setResizing] = useState(false);
  const sectionStyle = createMotionStyle(
    resizable && typeof contentHeight === 'number'
      ? contentHeight
      : animationDistance,
    headerHeight
  );
  const contentFrameStyle: SidebarExpandableContentFrameStyle | undefined =
    resizable && typeof contentHeight === 'number'
      ? {
          '--sidebar-expandable-content-height': `${contentHeight}px`,
        }
      : undefined;
  const hasHeaderEnd = Boolean(collapsedSummary || action);
  const toggleExpanded = useCallback(() => {
    onExpandedChange(!expanded);
  }, [expanded, onExpandedChange]);
  const handleHeaderKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggleExpanded();
    },
    [toggleExpanded]
  );
  const handleHeaderClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (
        !(event.target instanceof Node) ||
        !event.currentTarget.contains(event.target)
      ) {
        return;
      }
      if (isHeaderInteractiveTarget(event.target, event.currentTarget)) return;
      toggleExpanded();
    },
    [toggleExpanded]
  );

  const handleResizePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!resizable || !onContentHeightChange) return;
      event.preventDefault();
      resizeStartRef.current = {
        height: contentHeight ?? 0,
        pointerY: event.clientY,
      };
      resizeCurrentHeightRef.current = contentHeight ?? 0;
      setResizing(true);
      onResizeActiveChange?.(true);
    },
    [contentHeight, onContentHeightChange, onResizeActiveChange, resizable]
  );

  useEffect(() => {
    if (!resizing || !onContentHeightChange) return;

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const start = resizeStartRef.current;
      if (!start) return;
      const delta =
        placement === 'bottom'
          ? start.pointerY - event.clientY
          : event.clientY - start.pointerY;
      const nextHeight = start.height + delta;
      resizeCurrentHeightRef.current = nextHeight;
      onContentHeightChange(nextHeight);
    };
    const stopResize = () => {
      const nextHeight = resizeCurrentHeightRef.current;
      resizeStartRef.current = null;
      resizeCurrentHeightRef.current = null;
      resizePendingCommitHeightRef.current = nextHeight;
      setResizing(false);
      onResizeActiveChange?.(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
      if (resizeStartRef.current) {
        resizeStartRef.current = null;
        resizeCurrentHeightRef.current = null;
        onResizeActiveChange?.(false);
      }
    };
  }, [onContentHeightChange, onResizeActiveChange, placement, resizing]);

  useEffect(() => {
    if (resizing) return;
    const nextHeight = resizePendingCommitHeightRef.current;
    if (typeof nextHeight !== 'number') return;
    resizePendingCommitHeightRef.current = null;
    const animationFrame = requestAnimationFrame(() => {
      onContentHeightCommit?.(nextHeight);
    });
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [onContentHeightCommit, resizing]);

  const resizeHandleElement =
    resizable && expanded ? (
      <div
        aria-orientation="horizontal"
        className={styles.resizeHandle}
        data-resizing={resizing ? 'true' : undefined}
        onPointerDown={handleResizePointerDown}
        role="separator"
      />
    ) : null;
  const externalResizeHandleElement =
    resizable && expanded ? (
      <div
        aria-orientation="horizontal"
        className={styles.externalResizeHandle}
        data-resizing={resizing ? 'true' : undefined}
        onPointerDown={handleResizePointerDown}
        role="separator"
      />
    ) : null;

  const contentElement = (
    <div
      className={clsx(styles.content, contentClassName)}
      data-content-padding={contentPadding}
      style={contentStyle}
    >
      {children}
    </div>
  );
  const headerToggleElement = hasHeaderEnd ? (
    <button
      type="button"
      aria-expanded={expanded}
      className={styles.headerToggle}
      onClick={toggleExpanded}
    >
      <div className={clsx(styles.title, titleClassName)}>
        <span className={styles.chevron}>{'\u25B6'}</span>
        {title}
      </div>
    </button>
  ) : (
    <div className={styles.headerToggle}>
      <div className={clsx(styles.title, titleClassName)}>
        <span className={styles.chevron}>{'\u25B6'}</span>
        {title}
      </div>
    </div>
  );
  const collapsedSummaryElement = collapsedSummary ? (
    <div className={styles.collapsedSummary}>{collapsedSummary}</div>
  ) : null;

  return (
    <Fragment>
      <div
        className={clsx(styles.section, className)}
        data-expanded={expanded}
        data-placement={placement}
        data-resize-hit-area={resizeHitArea}
        data-resizable={resizable ? 'true' : undefined}
        data-resizing={resizing ? 'true' : undefined}
        data-testid={testId}
        style={sectionStyle}
      >
        <div
          ref={headerRef}
          aria-expanded={!hasHeaderEnd ? expanded : undefined}
          className={clsx(styles.header, headerClassName)}
          data-clickable-header="true"
          onClick={handleHeaderClick}
          onKeyDown={!hasHeaderEnd ? handleHeaderKeyDown : undefined}
          role={!hasHeaderEnd ? 'button' : undefined}
          tabIndex={!hasHeaderEnd ? 0 : undefined}
        >
          {headerToggleElement}
          {hasHeaderEnd ? (
            <div className={styles.headerEnd}>
              {collapsedSummaryElement}
              {action}
            </div>
          ) : null}
        </div>
        <div
          aria-hidden={!expanded}
          className={styles.contentFrame}
          inert={expanded ? undefined : true}
          style={contentFrameStyle}
        >
          {contentElement}
          {resizeHitArea === 'inside' ? resizeHandleElement : null}
        </div>
      </div>
      {resizeHitArea === 'inside-and-after'
        ? externalResizeHandleElement
        : null}
    </Fragment>
  );
};
